import {
  Avatar,
  Box,
  Chip,
  Grid,
  Typography,
  TextField,
  InputAdornment,
  Skeleton,
  DialogContent,
  DialogActions,
  useTheme,
} from '@mui/material'
import SendOutlinedIcon from '@mui/icons-material/SendOutlined'
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined'
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined'
import {
  useCloseTicketMutation,
  useReplyToTicketMutation,
  useMarkTicketRepliesSeenMutation,
} from '../../store/api/adminApi'
import { dayjsFormat, notify } from '../../shared/utilities/util'
import { useEffect, useRef, useState } from 'react'
import SharedDialog from '../../shared/components/Dialog'
import SharedButton from '../../shared/components/Button'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'

/**
 * Attachment URLs are tenant-supplied. `window.open` does NOT imply
 * `noopener`, so the opened page would receive `window.opener` and could
 * navigate this admin tab elsewhere (reverse tabnabbing). The scheme check
 * additionally blocks `javascript:` / `data:` payloads.
 */
const openAttachment = (url: string) => {
  try {
    const parsed = new URL(url, window.location.origin)
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return
    window.open(parsed.href, '_blank', 'noopener,noreferrer')
  } catch {
    notify('This attachment link is not valid', 'error')
  }
}

const ChatModal = ({
  open,
  ticketById,
  loadingTicketById,
  ticketByIdError = false,
  fetchTicketsById,
  onClose,
  selectTicket,
  companyName = '',
}) => {
  const [text, setText] = useState('')
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [closeTicketMut, { isLoading: closingTicket }] = useCloseTicketMutation()
  const [replyToTicket, { isLoading: sendingReply }] = useReplyToTicketMutation()
  const [markRepliesSeen] = useMarkTicketRepliesSeenMutation()
  const chatEndRef = useRef(null)
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'

  useEffect(() => {
    if (!loadingTicketById) {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [loadingTicketById, ticketById])

  // Clear the customer's unread flags once the thread is actually open. The
  // admin console never did this, so `isSeen` stayed false forever.
  const ticketIdForSeen = ticketById?._id
  const hasUnseenUserReply = ticketById?.replies?.some((reply: any) => reply.replyBy === 'user' && !reply.isSeen)
  useEffect(() => {
    if (ticketIdForSeen && hasUnseenUserReply) {
      markRepliesSeen({ ticketId: ticketIdForSeen })
    }
  }, [ticketIdForSeen, hasUnseenUserReply, markRepliesSeen])

  const handleCloseTicket = async () => {
    try {
      const res: any = await closeTicketMut({
        companyId: selectTicket?.companyId,
        ticketId: selectTicket?.ticketId,
      }).unwrap()
      notify(res?.message || 'Ticket closed successfully', 'success')
      setConfirmOpen(false)
      onClose(true)
    } catch (error) {
      // Keep the confirm dialog open so the admin can retry in context — the
      // interceptor has already surfaced the reason.
    }
  }

  const handleReplyQuery = async () => {
    const message = text.trim()
    // Guard re-entrancy: the send button and Enter handler both land here, and
    // a slow network otherwise lets a second press duplicate the reply.
    if (!message || sendingReply) return

    try {
      const res: any = await replyToTicket({
        companyId: selectTicket?.companyId,
        ticketId: selectTicket?.ticketId,
        message,
      }).unwrap()
      // Clear on success regardless of the response body — an empty 204 is
      // still a delivered reply, and keeping the text made admins re-send.
      setText('')
      fetchTicketsById()
      notify(res?.message || 'Message sent successfully', 'success')
    } catch (error) {}
  }

  // A failed fetch must not make the modal silently vanish while `open` is true.
  if (!ticketById && !loadingTicketById && !ticketByIdError) return null

  return (
    <>
      <SharedDialog
        variant="form"
        open={open}
        onClose={onClose}
        title="Support Ticket"
        subtitle={companyName || ticketById?.email}
        maxWidth={700}
        actions={
          ticketById?.status !== 3 ? (
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <SharedButton label="Close Ticket" btnVariant="danger" onClick={() => setConfirmOpen(true)} />
            </DialogActions>
          ) : null
        }
      >
        <DialogContent sx={{ px: 3, py: 0 }}>
          {loadingTicketById ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Header skeleton */}
              <Box
                sx={{
                  p: 2,
                  bgcolor: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc',
                  borderRadius: '12px',
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#e2e8f0'}`,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                }}
              >
                <Box>
                  <Skeleton variant="text" width={60} height={16} sx={{ mb: 0.5 }} />
                  <Skeleton variant="text" width="40%" height={22} />
                </Box>
                <Box sx={{ borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#e2e8f0'}`, pt: 1.5 }}>
                  <Skeleton variant="text" width={70} height={16} sx={{ mb: 0.5 }} />
                  <Skeleton variant="text" width="100%" height={18} />
                  <Skeleton variant="text" width="75%" height={18} />
                </Box>
              </Box>
              {/* Chat skeletons */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Skeleton variant="rectangular" width={250} height={60} sx={{ borderRadius: '10px' }} />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <Skeleton variant="rectangular" width={280} height={60} sx={{ borderRadius: '10px' }} />
              </Box>
            </Box>
          ) : !ticketById ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <ErrorOutlineIcon sx={{ fontSize: 34, color: 'text.secondary', mb: 1 }} />
              <Typography sx={{ fontSize: '15px', fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                Could not load this ticket
              </Typography>
              <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mb: 2 }}>
                The request failed. It may be a temporary network issue.
              </Typography>
              <SharedButton label="Retry" size="small" onClick={() => fetchTicketsById()} />
            </Box>
          ) : (
            <>
              {/* Ticket ID & Badges */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 2,
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                <Typography sx={{ fontSize: '18px', fontWeight: 700, color: 'text.primary' }}>
                  #{ticketById.requestId || '--'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {ticketById.issueType && (
                    <Chip
                      label={ticketById.issueType}
                      size="small"
                      sx={{
                        fontSize: '11px',
                        fontWeight: 700,
                        borderRadius: '6px',
                        bgcolor: isDark ? 'rgba(239,68,68,0.1)' : '#fef2f2',
                        color: isDark ? '#f87171' : '#dc2626',
                        border: `1px solid ${isDark ? 'rgba(239,68,68,0.2)' : '#fecaca'}`,
                      }}
                    />
                  )}
                  <Chip
                    icon={<FiberManualRecordIcon sx={{ fontSize: '8px !important' }} />}
                    label={
                      ticketById.status === 1
                        ? 'Open'
                        : ticketById.status === 2
                        ? 'Replied'
                        : ticketById.status === 3
                        ? 'Closed'
                        : 'Created'
                    }
                    size="small"
                    sx={{
                      fontSize: '11px',
                      fontWeight: 600,
                      borderRadius: '6px',
                      bgcolor: isDark ? 'rgba(255,255,255,0.05)' : '#fff',
                      color: 'text.primary',
                      border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`,
                      '& .MuiChip-icon': {
                        color: ticketById.status === 1 ? '#3b82f6' : ticketById.status === 2 ? '#16a34a' : '#dc2626',
                        ml: 0.5,
                      },
                    }}
                  />
                </Box>
              </Box>

              {/* Info Grid */}
              <Box
                sx={{
                  mb: 2,
                  p: 2,
                  borderRadius: '12px',
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#e2e8f0'}`,
                  borderLeft: `3px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`,
                  bgcolor: isDark ? 'rgba(255,255,255,0.02)' : '#fafbfc',
                }}
              >
                <Grid container spacing={2}>
                  {companyName && (
                    <Grid item xs={6}>
                      <Typography
                        sx={{
                          fontSize: '11px',
                          fontWeight: 700,
                          color: isDark ? '#94a3b8' : '#1e293b',
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                          mb: 0.5,
                        }}
                      >
                        Company
                      </Typography>
                      <Typography sx={{ fontSize: '13px', fontWeight: 500, color: 'text.primary' }}>
                        {companyName}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={6}>
                    <Typography
                      sx={{
                        fontSize: '11px',
                        fontWeight: 700,
                        color: isDark ? '#94a3b8' : '#1e293b',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        mb: 0.5,
                      }}
                    >
                      Email
                    </Typography>
                    <Typography
                      sx={{ fontSize: '13px', fontWeight: 500, color: 'text.primary', wordBreak: 'break-all' }}
                    >
                      {ticketById.email || '--'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography
                      sx={{
                        fontSize: '11px',
                        fontWeight: 700,
                        color: isDark ? '#94a3b8' : '#1e293b',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        mb: 0.5,
                      }}
                    >
                      Subject
                    </Typography>
                    <Typography sx={{ fontSize: '13px', fontWeight: 500, color: 'text.primary' }}>
                      {ticketById.subject || '--'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography
                      sx={{
                        fontSize: '11px',
                        fontWeight: 700,
                        color: isDark ? '#94a3b8' : '#1e293b',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        mb: 0.5,
                      }}
                    >
                      Created
                    </Typography>
                    <Typography sx={{ fontSize: '13px', fontWeight: 500, color: 'text.primary' }}>
                      {dayjsFormat(ticketById.createdAt, 'MMM DD, YYYY h:mm A')}
                    </Typography>
                  </Grid>
                  {ticketById.routePath && (
                    <Grid item xs={12}>
                      <Typography
                        sx={{
                          fontSize: '11px',
                          fontWeight: 700,
                          color: isDark ? '#94a3b8' : '#1e293b',
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                          mb: 0.5,
                        }}
                      >
                        Page
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '12px',
                          fontFamily: 'monospace',
                          color: 'text.secondary',
                          bgcolor: isDark ? 'rgba(255,255,255,0.04)' : '#f1f5f9',
                          px: 1,
                          py: 0.25,
                          borderRadius: '4px',
                          display: 'inline-block',
                          wordBreak: 'break-all',
                        }}
                      >
                        {ticketById.routePath}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>

              {/* Description */}
              {ticketById.initialMessage && (
                <Box
                  sx={{
                    mb: 2,
                    p: 2,
                    borderRadius: '12px',
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#e2e8f0'}`,
                    borderLeft: `3px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`,
                    bgcolor: isDark ? 'rgba(255,255,255,0.02)' : '#fafbfc',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '11px',
                      fontWeight: 700,
                      color: isDark ? '#64748b' : '#94a3b8',
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      mb: 0.75,
                    }}
                  >
                    Description
                  </Typography>
                  <Typography sx={{ fontSize: '13px', color: 'text.primary', lineHeight: 1.6 }}>
                    {ticketById.initialMessage}
                  </Typography>
                </Box>
              )}

              {/* Attachments */}
              {ticketById.images?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{
                      fontSize: '13px',
                      fontWeight: 700,
                      color: 'text.primary',
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      mb: 1.5,
                    }}
                  >
                    Attachments ({ticketById.images.length})
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    {ticketById.images.map((url: string, i: number) => (
                      <Box
                        key={i}
                        onClick={() => openAttachment(url)}
                        sx={{
                          width: 100,
                          height: 80,
                          borderRadius: '10px',
                          border: `2px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`,
                          overflow: 'hidden',
                          cursor: 'pointer',
                          position: 'relative',
                          transition: 'border-color 0.2s',
                          '&:hover': {
                            borderColor: isDark ? '#60a5fa' : '#3b82f6',
                            '& .attachment-overlay': { opacity: 1 },
                          },
                        }}
                      >
                        <Box
                          component="img"
                          src={url}
                          alt={`Attachment ${i + 1}`}
                          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                        <Box
                          className="attachment-overlay"
                          sx={{
                            position: 'absolute',
                            inset: 0,
                            bgcolor: 'rgba(0,0,0,0.45)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: 0,
                            transition: 'opacity 0.2s',
                          }}
                        >
                          <OpenInNewOutlinedIcon sx={{ fontSize: 24, color: '#fff' }} />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Conversation */}
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 700,
                  color: 'text.primary',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  mb: 1.5,
                }}
              >
                Conversation
              </Typography>

              {/* Chat Messages */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  mb: 2,
                  maxHeight: '300px',
                  overflowY: 'auto',
                  p: 2,
                  borderRadius: '12px',
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#e2e8f0'}`,
                  borderLeft: `3px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`,
                  bgcolor: isDark ? 'rgba(255,255,255,0.02)' : '#fafbfc',
                }}
              >
                {(!ticketById?.replies || ticketById.replies.length === 0) && (
                  <Typography sx={{ fontSize: '13px', color: 'text.secondary', textAlign: 'center', py: 3 }}>
                    No messages yet
                  </Typography>
                )}
                {ticketById?.replies?.map((reply, index: number) => {
                  const isSupport = reply?.replyBy === 'support'
                  return (
                    <Box
                      key={`${reply?.date}-${reply?.replyBy}-${index}`}
                      sx={{
                        display: 'flex',
                        flexDirection: isSupport ? 'row-reverse' : 'row',
                        alignItems: 'flex-end',
                        gap: 1,
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 28,
                          height: 28,
                          flexShrink: 0,
                          bgcolor: isSupport
                            ? isDark
                              ? 'rgba(59,130,246,0.15)'
                              : '#dbeafe'
                            : isDark
                            ? 'rgba(255,255,255,0.08)'
                            : '#e2e8f0',
                        }}
                      >
                        {isSupport ? (
                          <SupportAgentOutlinedIcon sx={{ fontSize: 15, color: isDark ? '#60a5fa' : '#2563eb' }} />
                        ) : (
                          <PersonOutlineOutlinedIcon sx={{ fontSize: 15, color: isDark ? '#94a3b8' : '#64748b' }} />
                        )}
                      </Avatar>
                      <Box sx={{ maxWidth: '80%' }}>
                        <Box
                          sx={{
                            bgcolor: isSupport
                              ? isDark
                                ? 'rgba(59,130,246,0.1)'
                                : '#2563eb'
                              : isDark
                              ? 'rgba(255,255,255,0.06)'
                              : '#fff',
                            border: isSupport ? 'none' : `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`,
                            px: 2,
                            py: 1.25,
                            borderRadius: isSupport ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: '13px',
                              color: isSupport ? (isDark ? '#e2e8f0' : '#fff') : 'text.primary',
                              lineHeight: 1.5,
                            }}
                          >
                            {reply?.message || ''}
                          </Typography>
                        </Box>
                        <Typography
                          sx={{
                            fontSize: '10px',
                            color: isDark ? '#475569' : '#94a3b8',
                            mt: 0.5,
                            px: 0.5,
                            textAlign: isSupport ? 'right' : 'left',
                          }}
                        >
                          {dayjsFormat(reply?.date, 'MMM DD, YYYY h:mm A') || '--'}
                        </Typography>
                      </Box>
                    </Box>
                  )
                })}
                <div ref={chatEndRef}></div>
              </Box>

              {/* Input field */}
              <Box sx={{ position: 'relative', mb: 2 }}>
                <TextField
                  multiline
                  minRows={1}
                  maxRows={4}
                  placeholder={
                    ticketById.status === 3
                      ? 'This ticket is closed'
                      : 'Type your reply... (Enter to send, Shift+Enter for new line)'
                  }
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault()
                      if (ticketById.status !== 3) handleReplyQuery()
                    }
                  }}
                  disabled={ticketById.status === 3}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      fontSize: '14px',
                      pr: 7,
                      bgcolor: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc',
                      '& fieldset': {
                        borderColor: isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0',
                      },
                      '&:hover fieldset': {
                        borderColor: isDark ? 'rgba(255,255,255,0.2)' : '#cbd5e1',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: isDark ? '#60a5fa' : '#2563eb',
                      },
                      '&.Mui-disabled': {
                        cursor: 'not-allowed',
                        '& *': { cursor: 'not-allowed' },
                        '& .MuiInputBase-input': {
                          WebkitTextFillColor: isDark ? '#94a3b8' : '#1e293b',
                        },
                      },
                    },
                  }}
                />
                {ticketById.status !== 3 && (
                  <Box
                    component="button"
                    type="button"
                    aria-label="Send reply"
                    disabled={!text.trim() || sendingReply}
                    onClick={handleReplyQuery}
                    sx={{
                      position: 'absolute',
                      right: 8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: 36,
                      height: 36,
                      borderRadius: '8px',
                      border: 'none',
                      cursor: text.trim() ? 'pointer' : 'default',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: text.trim()
                        ? isDark
                          ? '#3b82f6'
                          : '#2563eb'
                        : isDark
                        ? 'rgba(255,255,255,0.06)'
                        : '#e2e8f0',
                      transition: 'all 0.2s ease',
                      '&:hover': text.trim()
                        ? {
                            bgcolor: isDark ? '#2563eb' : '#1d4ed8',
                          }
                        : {},
                    }}
                  >
                    <SendOutlinedIcon
                      sx={{ fontSize: 18, color: text.trim() ? '#fff' : isDark ? '#475569' : '#94a3b8' }}
                    />
                  </Box>
                )}
              </Box>
            </>
          )}
        </DialogContent>
      </SharedDialog>

      {/* Confirmation Dialog */}
      <SharedDialog
        variant="confirm"
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        icon={<ErrorOutlineIcon sx={{ fontSize: 28, color: '#ef4444' }} />}
        iconBg={isDark ? 'rgba(239,68,68,0.15)' : '#fef2f2'}
        title="Close Ticket"
        description="Are you sure you want to close this ticket? This action cannot be undone."
        cancelLabel="Cancel"
        confirmLabel="Close Ticket"
        confirmColor="error"
        confirmLoading={closingTicket}
        onConfirm={handleCloseTicket}
      />
    </>
  )
}

export default ChatModal
